import React from "react";
import { Sparkle, Factory, Truck } from "@phosphor-icons/react";

export default function EconomicsTab({ formula: f, computePricing, busy }) {
    const p = f.pricing;
    return (
        <div className="max-w-4xl space-y-8" data-testid="economics-tab">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <p className="text-sm text-[var(--ink-2)] max-w-xl">
                    Comparativa de PVP con el mercado español, estimación de coste de producción y plan de
                    comercialización con distribuidores por volumen de pedido.
                </p>
                {f.feasibility !== "rejected" && (
                    <button onClick={computePricing} disabled={busy}
                        className="px-3 py-2 border hairline text-xs bg-white hover:bg-[var(--cream)] inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="formula-pricing-button">
                        <Sparkle size={12}/> {busy ? "Analizando…" : (p ? "Recalcular análisis" : "Analizar economía")}
                    </button>
                )}
            </div>

            {!p ? (
                <div className="border hairline bg-white p-6 text-sm text-[var(--ink-3)]" data-testid="formula-pricing-empty">
                    {f.feasibility === "approved" && f.status !== "approved"
                        ? <span className="inline-flex items-center gap-2"><Sparkle size={12} className="animate-pulse"/> Calculando análisis económico en segundo plano… (se mostrará aquí cuando termine)</span>
                        : "Aún no se ha calculado el análisis económico. Lánzalo para obtener PVP, coste de producción y plan de distribuidores."}
                </div>
            ) : (
                <>
                    {/* PVP mercado */}
                    <section data-testid="economics-pvp-section">
                        <div className="label-eyebrow mb-3">PVP · mercado español</div>
                        <div className="border hairline bg-white">
                            <div className="grid sm:grid-cols-4 gap-px bg-[var(--line)]">
                                <PriceCell label="Mín. mercado" v={p.market_min} currency={p.currency}/>
                                <PriceCell label="Máx. mercado" v={p.market_max} currency={p.currency}/>
                                <PriceCell label="Media mercado" v={p.market_avg} currency={p.currency}/>
                                <PriceCell label="PVP recomendado" v={p.recommended_price} currency={p.currency} highlight/>
                            </div>
                            <div className="p-5 space-y-3">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <span className="citation-badge bg-[var(--ink)] text-white border-[var(--ink)] uppercase">{p.tier}</span>
                                    {p.differentiation_premium_pct != null && (
                                        <span className="text-xs text-[var(--ink-2)] mono">Prima por exclusividad: <strong>+{p.differentiation_premium_pct}%</strong></span>
                                    )}
                                </div>
                                <p className="text-sm leading-relaxed">{p.rationale}</p>
                                {p.similar_products?.length > 0 && (
                                    <div>
                                        <div className="label-eyebrow mb-2 mt-3">Productos similares detectados</div>
                                        <ul className="space-y-1.5">
                                            {p.similar_products.map((sp, i) => (
                                                <li key={i} className="text-sm flex items-center justify-between gap-3 py-1.5 border-b hairline last:border-b-0">
                                                    <a href={sp.url} target="_blank" rel="noreferrer" className="text-[var(--sage)] hover:underline truncate">{sp.title}</a>
                                                    <span className="mono text-xs text-[var(--ink-2)] flex-shrink-0">{sp.est_price ? `${sp.est_price} ${p.currency}` : "—"}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Coste de producción */}
                    {p.production_cost && (
                        <section data-testid="economics-production-section">
                            <div className="label-eyebrow mb-3 flex items-center gap-2"><Factory size={13}/> Coste de producción estimado · {p.production_cost.units_basis}</div>
                            <div className="border hairline bg-white p-5 space-y-3">
                                <table className="w-full text-sm">
                                    <tbody>
                                        {(p.production_cost.per_component || []).map((c, i) => (
                                            <tr key={i} className="border-b hairline">
                                                <td className="py-2">{c.name} {c.dose_mg ? <span className="mono text-xs text-[var(--ink-3)]">({c.dose_mg} mg)</span> : null}</td>
                                                <td className="py-2 text-right mono">{c.est_cost_eur != null ? `${c.est_cost_eur.toFixed ? c.est_cost_eur.toFixed(2) : c.est_cost_eur} €` : "—"}</td>
                                            </tr>
                                        ))}
                                        {p.production_cost.manufacturing_overhead_eur != null && (
                                            <tr className="border-b hairline text-[var(--ink-2)]">
                                                <td className="py-2">Fabricación / encapsulado / control de calidad</td>
                                                <td className="py-2 text-right mono">{p.production_cost.manufacturing_overhead_eur} €</td>
                                            </tr>
                                        )}
                                        {p.production_cost.packaging_eur != null && (
                                            <tr className="border-b hairline text-[var(--ink-2)]">
                                                <td className="py-2">Envase y etiquetado</td>
                                                <td className="py-2 text-right mono">{p.production_cost.packaging_eur} €</td>
                                            </tr>
                                        )}
                                        <tr className="font-medium">
                                            <td className="py-2.5">Coste total por unidad</td>
                                            <td className="py-2.5 text-right mono text-base" data-testid="economics-total-cost">
                                                {p.production_cost.total_cost_per_unit_eur != null ? `${p.production_cost.total_cost_per_unit_eur} €` : "—"}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                {p.production_cost.assumptions && (
                                    <p className="text-xs text-[var(--ink-3)] mono">{p.production_cost.assumptions}</p>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Plan distribuidores */}
                    {p.distributor_plan?.tiers?.length > 0 && (
                        <section data-testid="economics-distributor-section">
                            <div className="label-eyebrow mb-3 flex items-center gap-2"><Truck size={13}/> Plan de comercialización con distribuidores</div>
                            <div className="border hairline bg-white overflow-x-auto">
                                <table className="w-full text-sm min-w-[640px]">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] border-b hairline bg-[var(--cream)]">
                                            <th className="text-left px-4 py-2.5">Pedido (uds)</th>
                                            <th className="text-right px-4 py-2.5">Coste/ud</th>
                                            <th className="text-right px-4 py-2.5">Precio distribuidor</th>
                                            <th className="text-right px-4 py-2.5">Margen distribuidor</th>
                                            <th className="text-right px-4 py-2.5">Margen productor</th>
                                            <th className="text-right px-4 py-2.5">Ingresos</th>
                                            <th className="text-right px-4 py-2.5">Beneficio</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {p.distributor_plan.tiers.map((t, i) => (
                                            <tr key={i} className="border-b hairline last:border-b-0" data-testid={`distributor-tier-${i}`}>
                                                <td className="px-4 py-2.5 font-medium mono">{(t.order_units || 0).toLocaleString("es-ES")}</td>
                                                <td className="px-4 py-2.5 text-right mono">{t.unit_cost_eur != null ? `${t.unit_cost_eur} €` : "—"}</td>
                                                <td className="px-4 py-2.5 text-right mono">{t.price_to_distributor_eur != null ? `${t.price_to_distributor_eur} €` : "—"}</td>
                                                <td className="px-4 py-2.5 text-right mono">{t.distributor_margin_pct != null ? `${t.distributor_margin_pct}%` : "—"}</td>
                                                <td className="px-4 py-2.5 text-right mono">{t.producer_margin_pct != null ? `${t.producer_margin_pct}%` : "—"}</td>
                                                <td className="px-4 py-2.5 text-right mono">{t.projection_revenue_eur != null ? `${t.projection_revenue_eur.toLocaleString("es-ES")} €` : "—"}</td>
                                                <td className="px-4 py-2.5 text-right mono font-medium">{t.projection_profit_eur != null ? `${t.projection_profit_eur.toLocaleString("es-ES")} €` : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {p.distributor_plan.strategy_notes && (
                                <p className="text-sm leading-relaxed mt-3 border hairline bg-white p-4">{p.distributor_plan.strategy_notes}</p>
                            )}
                        </section>
                    )}

                    {p && !p.production_cost && (
                        <div className="text-xs text-[var(--ink-3)]">
                            Este análisis fue calculado con la versión anterior — pulsa «Recalcular análisis» para obtener
                            coste de producción y plan de distribuidores.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function PriceCell({ label, v, currency, highlight }) {
    return (
        <div className={`p-5 ${highlight ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-white"}`}>
            <div className={`text-[10px] uppercase tracking-wider mb-2 ${highlight ? "text-[var(--bone)]/70" : "text-[var(--ink-2)]"}`}>{label}</div>
            <div className="font-display text-2xl">{v != null ? `${v} ${currency || "€"}` : "—"}</div>
        </div>
    );
}
